import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  setAccountantContextToken,
  setOnAccountantContextInvalid as setSharedContextInvalidHandler,
} from "@moonscape/shared";
import {
  setAccountantContextToken as setWebAxiosToken,
  setOnAccountantContextInvalid as setWebContextInvalidHandler,
} from "@/axios";
import { useHistory, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { trackEvent } from "@/analytics";
import { ACCOUNTANT_EVENTS } from "@/analytics/events";

interface AccountantViewState {
  /** True when an accountant is viewing a client's data */
  isViewingClient: boolean;
  /** Client name being viewed */
  clientName: string | null;
  /** Context token for API calls on behalf of client */
  contextToken: string | null;
  /** Client user ID */
  clientUserId: string | null;
  /** Enter client viewing mode */
  enterClientView: (token: string, clientName: string, clientUserId: string) => void;
  /** Exit client viewing mode */
  exitClientView: () => void;
}

const defaultState: AccountantViewState = {
  isViewingClient: false,
  clientName: null,
  contextToken: null,
  clientUserId: null,
  enterClientView: () => {},
  exitClientView: () => {},
};

export const AccountantViewContext = createContext<AccountantViewState>(defaultState);

export const useAccountantView = () => useContext(AccountantViewContext);

/** Convenience hook — returns true when in read-only client viewing mode */
export const useReadOnly = () => {
  const { isViewingClient } = useAccountantView();
  return isViewingClient;
};

export const AccountantViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const [state, setState] = useState<{
    isViewingClient: boolean;
    clientName: string | null;
    contextToken: string | null;
    clientUserId: string | null;
  }>({
    isViewingClient: false,
    clientName: null,
    contextToken: null,
    clientUserId: null,
  });

  const clearClientView = useCallback(() => {
    setAccountantContextToken(null);
    setWebAxiosToken(null);
    setState({ isViewingClient: false, clientName: null, contextToken: null, clientUserId: null });
  }, []);

  const enterClientView = (token: string, clientName: string, clientUserId: string) => {
    setAccountantContextToken(token);
    setWebAxiosToken(token);
    setState({ isViewingClient: true, clientName, contextToken: token, clientUserId });

    trackEvent(ACCOUNTANT_EVENTS.CONTEXT_SWITCHED, {
      action: "enter_client_view",
      client_userid: clientUserId,
    });
  };

  const exitClientView = useCallback(() => {
    if (state.isViewingClient) {
      trackEvent(ACCOUNTANT_EVENTS.CONTEXT_SWITCHED, {
        action: "exit_client_view",
        client_userid: state.clientUserId,
      });
    }
    clearClientView();
  }, [clearClientView, state.clientUserId, state.isViewingClient]);

  const handleInvalidContext = useCallback(() => {
    if (!state.isViewingClient) return;

    clearClientView();
    toast.error("Client viewing session expired. Re-enter from your client list.");
    history.push("/a/clients");
  }, [clearClientView, history, state.isViewingClient]);

  useEffect(() => {
    setSharedContextInvalidHandler(handleInvalidContext);
    setWebContextInvalidHandler(handleInvalidContext);
    return () => {
      setSharedContextInvalidHandler(null);
      setWebContextInvalidHandler(null);
    };
  }, [handleInvalidContext]);

  useEffect(() => {
    if (state.isViewingClient && location.pathname.startsWith("/a/")) {
      clearClientView();
    }
  }, [clearClientView, location.pathname, state.isViewingClient]);

  return (
    <AccountantViewContext.Provider value={{ ...state, enterClientView, exitClientView }}>
      {children}
    </AccountantViewContext.Provider>
  );
};
